import * as React from "react"
import * as TablerIcons from "@tabler/icons-react";
import { Slot } from "@radix-ui/react-slot"
import styles from "./MButton.module.css";

export type MButtonVariant = 'filled' | 'outline' | 'outline-dashed' | 'link' | 'icon-only-filled' | 'icon-only-outline' | 'destructive' | 'back';
export type MButtonState = 'default' | 'pressed' | 'disabled';
export type MButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface MButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: MButtonVariant;
  state?: MButtonState;
  size?: MButtonSize;
  text?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  // Deprecated: for backward compatibility
  icon?: string | React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const renderIcon = (icon?: string | React.ReactNode, size: MButtonSize = 'sm') => {
  if (!icon) return null;
  const iconClass = [styles.mbutton__icon, styles[`mbutton__icon--${size}`]].filter(Boolean).join(' ');
  if (typeof icon === 'string') {
    const iconName = 'Icon' + icon.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('');
    const IconComponent = (TablerIcons as any)[iconName];
    if (IconComponent) return <IconComponent className={iconClass} />;
    return null;
  }
  return <span className={iconClass}>{icon}</span>;
};

import { IconChevronLeft } from "@tabler/icons-react";

const MButton = React.forwardRef<HTMLButtonElement, MButtonProps>(
  ({
    className,
    variant = 'filled',
    size = 'sm',
    state = 'default',
    text,
    leftIcon,
    rightIcon,
    // Deprecated
    icon, // for backward compatibility
    asChild = false,
    children,
    disabled,
    onClick,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const isIconOnly = variant === 'icon-only-filled' || variant === 'icon-only-outline';
    // Use disabled prop to override state
    const finalState = disabled ? 'disabled' : state;
    
    // WKWebView fix: blur focused element after click
    // Delay blur to avoid interfering with React click event handling
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);

      if (typeof document === 'undefined') return;

      setTimeout(() => {
        const el = document.activeElement;
        if (el instanceof HTMLElement) {
          el.blur();
        }
      }, 0);
    }; 
    
    if (variant === 'back') {
      // Only one size for back variant (md), ignore size prop
      // Match .profile-header-back-btn: flex row, align center, gap 6px, color var(--colour-text-default), no hover/active/focus
      const backLabel = text || children || 'Back';
      return (
        <button
          type="button"
          className={styles.mbuttonBack}
          ref={ref}
          aria-label="Back"
          onClick={handleClick}
          {...props}
        >
          <IconChevronLeft size={20} style={{ color: 'var(--colour-text-default)' }} />
          <span className={styles.mbuttonBackLabel}>{backLabel}</span>
        </button>
      );
    }
    return (
      <Comp
        className={[
          styles.mbutton,
          styles[`mbutton--${variant}`],
          styles[`mbutton--${size}`],
          styles[`mbutton--${finalState}`],
          className
        ].filter(Boolean).join(' ')}
        ref={ref}
        disabled={disabled}
        onClick={handleClick}
        {...props}
      >
        {isIconOnly
          ? renderIcon(leftIcon || icon, size)
          : <>
              {leftIcon && <span style={{ marginRight: 4, display: 'inline-flex', alignItems: 'center' }}>{renderIcon(leftIcon, size)}</span>}
              {text || children}
              {rightIcon && <span style={{ marginLeft: 4, display: 'inline-flex', alignItems: 'center' }}>{renderIcon(rightIcon, size)}</span>}
            </>
        }
      </Comp>
    );
  }
)

MButton.displayName = "MButton"

export { MButton }
